import { useEffect, useState } from "react";
import { api, inr, stageLabel, TASK_STATUSES } from "../lib/api";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PriorityBadge } from "../components/Badges";
import { AlertTriangle, CheckCircle2, Clock, Wallet, PackageSearch, Calendar, Phone, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

function Section({ title, icon: Icon, count, children, tone = "primary" }) {
  const tones = {
    primary: "text-primary border-primary/30",
    critical: "text-red-400 border-red-500/30",
    warning: "text-amber-400 border-amber-500/30",
    success: "text-emerald-400 border-emerald-500/30",
  };
  return (
    <Card className="p-5 bg-card border-border">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`h-7 w-7 rounded border grid place-items-center ${tones[tone]} bg-secondary/40`}>
            <Icon className="h-3.5 w-3.5" />
          </div>
          <h3 className="font-display font-semibold">{title}</h3>
        </div>
        <span className="text-xs font-mono text-muted-foreground">{count}</span>
      </div>
      <div className="space-y-2 max-h-72 overflow-y-auto pr-1">{children}</div>
    </Card>
  );
}

export default function DailyReview() {
  const [data, setData] = useState(null);
  const [projects, setProjects] = useState([]);

  const reload = () => {
    api.get("/dashboard/summary").then((r) => setData(r.data));
    api.get("/projects").then((r) => setProjects(r.data));
  };
  useEffect(() => { reload(); }, []);

  if (!data) return <div className="text-muted-foreground">Loading…</div>;

  const today = new Date().toISOString().slice(0, 10);
  const stuck = projects.filter((p) => p.due_date && p.due_date < today && p.stage !== "completed");
  const paymentPending = projects.filter((p) => p.payment_status === "pending" || p.payment_status === "partial");
  const installsToday = projects.filter((p) => p.installation_status === "scheduled" || p.installation_status === "in_progress");
  const followUps = projects.filter((p) => ["proposal_sent", "commercial_negotiation", "requirement_discussion"].includes(p.stage));

  return (
    <div className="space-y-4" data-testid="daily-review-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Morning briefing</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Daily Review</h1>
        <p className="text-sm text-muted-foreground mt-1">Everything Tanmay needs to scan in 3 minutes.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Section title="Due Today" icon={Clock} count={data.today_tasks.length}>
          {data.today_tasks.length === 0 ? <Empty text="Clear plate." /> :
            data.today_tasks.map((t) => (
              <TaskRow key={t.id} task={t} onChanged={reload} />
            ))}
        </Section>

        <Section title="Overdue Tasks" icon={AlertTriangle} tone="critical" count={data.overdue_tasks.length}>
          {data.overdue_tasks.length === 0 ? <Empty text="Nothing overdue." /> :
            data.overdue_tasks.map((t) => (
              <TaskRow key={t.id} task={t} onChanged={reload} overdue />
            ))}
        </Section>

        <Section title="Stuck Projects" icon={AlertTriangle} tone="warning" count={stuck.length}>
          {stuck.length === 0 ? <Empty text="Everything moving." /> :
            stuck.map((p) => (
              <Link key={p.id} to={`/projects/${p.id}`} className="block">
                <Row title={p.client_name} sub={`${stageLabel(p.stage)} · Due ${p.due_date}`}>
                  <span className="text-xs font-mono text-primary">{inr(p.project_value)}</span>
                </Row>
              </Link>
            ))}
        </Section>

        <Section title="Payments Pending" icon={Wallet} tone="critical" count={paymentPending.length}>
          {paymentPending.length === 0 ? <Empty text="All paid up." /> :
            paymentPending.map((p) => (
              <Row key={p.id} title={p.client_name} sub={`${p.payment_status.toUpperCase()} · ${stageLabel(p.stage)}`}>
                <span className="text-xs font-mono text-primary">{inr(p.project_value)}</span>
              </Row>
            ))}
        </Section>

        <Section title="Material Pending" icon={PackageSearch} tone="warning" count={data.pending_procurement.length}>
          {data.pending_procurement.length === 0 ? <Empty text="No orders in flight." /> :
            data.pending_procurement.map((p) => (
              <Row key={p.id} title={p.item_name} sub={`${p.vendor || "—"} · ETA ${p.expected_delivery || "TBD"}`}>
                <span className="text-xs capitalize text-muted-foreground">{p.status}</span>
              </Row>
            ))}
        </Section>

        <Section title="Installations Scheduled" icon={Calendar} count={installsToday.length}>
          {installsToday.length === 0 ? <Empty text="Nothing scheduled." /> :
            installsToday.map((p) => (
              <Row key={p.id} title={p.client_name} sub={`${p.city} · ${p.installation_status.replace("_", " ")}`}>
                <span className="text-xs font-mono">{p.due_date || ""}</span>
              </Row>
            ))}
        </Section>

        <Section title="Client Follow-ups" icon={Phone} count={followUps.length}>
          {followUps.length === 0 ? <Empty text="No follow-ups pending." /> :
            followUps.map((p) => (
              <Row key={p.id} title={p.client_name} sub={`${stageLabel(p.stage)} · ${p.next_action || ""}`}>
                <span className="text-xs font-mono text-primary">{inr(p.project_value)}</span>
              </Row>
            ))}
        </Section>

        <Section title="Awaiting Your Approval" icon={ShieldCheck} tone="warning" count={data.pending_approvals.length}>
          {data.pending_approvals.length === 0 ? <Empty text="Inbox zero." /> :
            data.pending_approvals.map((a) => (
              <Row key={a.id} title={a.title} sub={a.type}><span className="text-xs font-mono text-primary">{a.amount ? inr(a.amount) : ""}</span></Row>
            ))}
        </Section>

        <Section title="Open Service Tickets" icon={CheckCircle2} tone="primary" count={data.open_tickets.length}>
          {data.open_tickets.length === 0 ? <Empty text="No open tickets." /> :
            data.open_tickets.map((t) => (
              <Row key={t.id} title={t.title} sub={`${t.client_name} · ${t.status}`}><PriorityBadge value={t.priority} /></Row>
            ))}
        </Section>
      </div>
    </div>
  );
}

function Row({ title, sub, children }) {
  return (
    <div className="flex items-center gap-3 p-2.5 rounded border border-border bg-secondary/30 card-hover">
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{title}</div>
        <div className="text-xs text-muted-foreground truncate">{sub}</div>
      </div>
      {children}
    </div>
  );
}

function TaskRow({ task, onChanged, overdue }) {
  const changeStatus = async (v) => {
    try {
      await api.patch(`/tasks/${task.id}`, { status: v });
      toast.success("Updated");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };
  return (
    <div className={`flex items-center gap-2 p-2 rounded border ${overdue ? "border-red-500/20 bg-red-500/5" : "border-border bg-secondary/30"}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{task.title}</div>
        <div className="text-xs text-muted-foreground truncate">{task.function} · {overdue ? `Was due ${task.due_date}` : "Due today"}</div>
      </div>
      <PriorityBadge value={task.priority} />
      <Select value={task.status} onValueChange={changeStatus}>
        <SelectTrigger className="h-7 w-[128px]"><SelectValue /></SelectTrigger>
        <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
}
function Empty({ text }) {
  return <div className="text-sm text-muted-foreground text-center py-6 border border-dashed border-border rounded">{text}</div>;
}
